import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useKeyboardOffset } from '../utils/useKeyboardOffset';
import { SIZING } from '../constants/ui';
import { ActionFooter } from './ActionFooter';

interface FooterProps {
  charPercentage: number;
  isOverLimit: boolean;
  onPost: () => void;
  hasContent: boolean;
  posting: boolean;
  content: string;
  charCount?: number;
  maxChars?: number;  
  primaryActionLabel?: string;
  onAddAttachments?: (files: File[]) => void;
  canAddAttachments?: boolean;
  addAttachmentsDisabledReason?: string;
  attachments?: Array<{ type: 'image'; url?: string; preview?: string; alt?: string; uploading?: boolean; error?: string }>;
  isUploadingAttachments?: boolean;
}

const ImageAttachButton: React.FC<{
  canAddAttachments: boolean;
  disabledReason: string;
  attachments: Array<{ type: 'image'; url?: string; preview?: string; alt?: string; uploading?: boolean; error?: string }>;
  onAddAttachments: (files: File[]) => void;
}> = ({ canAddAttachments, disabledReason, attachments, onAddAttachments }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ bottom: number } | null>(null);
  const tooltipTimer = useRef<ReturnType<typeof setTimeout>>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isAtLimit = (attachments || []).filter(a => a.url || a.preview || a.uploading).length >= SIZING.MAX_ATTACHMENTS;
  const isDisabled = !canAddAttachments || isAtLimit;

  useEffect(() => {
    return () => { if (tooltipTimer.current) clearTimeout(tooltipTimer.current); };
  }, []);

  const showTip = useCallback(() => {
    if (!isDisabled) return;
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPos({ bottom: window.innerHeight - rect.top + 8 });
    }
    setShowTooltip(true);
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    tooltipTimer.current = setTimeout(() => setShowTooltip(false), 2500);
  }, [isDisabled]);

  return (
    <span
      className="image-button-wrapper"
      onMouseEnter={showTip}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!canAddAttachments || isAtLimit) {
            showTip();
            return;
          }
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.multiple = true;
          input.onchange = () => {
            const files = Array.from(input.files || []);
            if (files.length > 0) onAddAttachments(files);
          };
          input.click();
        }}
        className={`footer-icon-button image-button ${isDisabled ? 'disabled' : ''}`}
        aria-disabled={isDisabled}
        aria-label={isDisabled ? disabledReason : 'Add images'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-image">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      </button>
      {showTooltip && isDisabled && tooltipPos && createPortal(
        <span
          className="image-button-tooltip"
          role="tooltip"
          style={{ bottom: tooltipPos.bottom }}
        >
          {!canAddAttachments ? disabledReason : `Max ${SIZING.MAX_ATTACHMENTS} images`}
        </span>,
        document.body
      )}
    </span>
  );
};

const Footer: React.FC<FooterProps> = ({ 
  charPercentage, 
  isOverLimit, 
  onPost, 
  hasContent,
  posting,
  content,
  charCount,
  maxChars,
  primaryActionLabel = 'Post',
  onAddAttachments,
  canAddAttachments = true,
  addAttachmentsDisabledReason = 'Connect a platform to attach images',
  attachments = [],
  isUploadingAttachments
}) => {
  const [copied, setCopied] = useState(false);
  const kbOffset = useKeyboardOffset();

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [content]);

  return kbOffset > 0 ? null : (
    <ActionFooter
      className="editor-footer"
      position="fixed"
    >
      <div className="footer-left-group">
        <div className="character-count-wheel">
          <svg viewBox="0 0 20 20" className={`wheel ${isOverLimit ? 'over-limit' : ''}`}>
            {isOverLimit && (
              <circle
                r="6"
                cx="10"
                cy="10"
                className="wheel-fill"
              />
            )}
            <circle
              r="8"
              cx="10"
              cy="10"
              fill="none"
              strokeWidth="2"
              className="wheel-bg"
            />
            <circle
              r="8"
              cx="10"
              cy="10"
              fill="none"
              strokeWidth="2"
              className="wheel-progress"
              style={{
                strokeDasharray: `${(charPercentage * 50.24) / 100} 50.24`,
                transform: 'rotate(-90deg)',
                transformOrigin: 'center'
              }}
            />
          </svg>
        </div>
        {isOverLimit && charCount !== undefined && maxChars !== undefined && (
          <div className="char-over-limit">
            +{charCount - maxChars}
          </div>
        )}
      </div>
      
      <div className="post-actions">
        <div className="editor-footer__utility-actions">
          {/* Image button first (available when attachment handler is provided) */}
          {onAddAttachments && (
            <ImageAttachButton
              canAddAttachments={canAddAttachments}
              disabledReason={addAttachmentsDisabledReason}
              attachments={attachments}
              onAddAttachments={onAddAttachments}
            />
          )}

          {/* Copy button next */}
          <button 
            type="button"
            className={`footer-icon-button copy-button${(!hasContent) ? ' disabled' : ''}`} 
            onClick={copyToClipboard} 
            disabled={!hasContent}
            title="Copy to clipboard"
            style={{
              opacity: !hasContent ? 0.5 : 1,
              cursor: !hasContent ? 'not-allowed' : 'pointer',
              pointerEvents: !hasContent ? 'none' : 'auto',
            }}
          >
            {copied ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            )}
          </button>
        </div>
        <button 
          type="button"
          className={`post-button action-footer__button action-footer__button--primary${(!hasContent || posting || isUploadingAttachments) ? ' disabled' : ''}`} 
          onClick={() => {
            onPost();
          }}
          disabled={!hasContent || posting || !!isUploadingAttachments}
          style={{
            opacity: (!hasContent || isUploadingAttachments) ? 0.5 : 1,
            cursor: (!hasContent || isUploadingAttachments) ? 'not-allowed' : 'pointer',
            pointerEvents: (!hasContent || posting || isUploadingAttachments) ? 'none' : 'auto',
          }}
        >
          {posting ? 'Posting...' : (isUploadingAttachments ? 'Uploading…' : primaryActionLabel)}
        </button>
      </div>
    </ActionFooter>
  );
};

export default Footer; 
